'use client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Flame } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { StarIcon } from './medallions/star-icon'

export interface AtAGlanceCardProps {
  progress: { reported: number; clicked: number; missed: number }  // absolute counts
  streak: { current: number; best: number }
  pillarSlam: { done: number; total: number }
}

export function AtAGlanceCard({ progress, streak, pillarSlam }: AtAGlanceCardProps) {
  const { t } = useTranslation()
  const { reported, clicked, missed } = progress
  const total = reported + clicked + missed
  const safeTotal = total || 1  // guard: 0/0 → NaN → broken SVG strokeDashoffset
  const C = 2 * Math.PI * 44
  const reportedLen = (reported / safeTotal) * C
  const clickedLen = (clicked / safeTotal) * C
  const missedLen = (missed / safeTotal) * C

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle>{t('learner.cards.atAGlance.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {/* Donut */}
          <div className="col-span-2 flex items-center gap-5 rounded-xl border border-border bg-muted/40 p-5">
            <div className="relative flex-shrink-0">
              <svg viewBox="0 0 120 120" width={144} height={144} className="-rotate-90">
                <circle cx="60" cy="60" r="44" fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth="12" className="text-muted-foreground" />
                <circle cx="60" cy="60" r="44" fill="none" stroke="#10b981" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${reportedLen} ${C}`} />
                <circle cx="60" cy="60" r="44" fill="none" stroke="#f59e0b" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${clickedLen} ${C}`} strokeDashoffset={-reportedLen} />
                <circle cx="60" cy="60" r="44" fill="none" stroke="#ef4444" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${missedLen} ${C}`} strokeDashoffset={-(reportedLen + clickedLen)} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold tabular-nums text-foreground">{total > 0 ? Math.round((reported / total) * 100) : 0}%</div>
                <div className="text-[10px] text-muted-foreground">{t('learner.cards.atAGlance.passedShare')}</div>
              </div>
            </div>
            {/* These buckets are PhishTrainer ROUND outcomes (passed / partial /
                failed), not phishing-campaign actions. They were mislabeled
                "reported / clicked / missed" — phishing-send vocabulary — which
                read as "0% reported" for a learner who simply hadn't passed
                rounds yet (#1604). The prop keys stay (data shape) but the
                user-facing labels now match the training-round semantics. */}
            <div className="flex-1 space-y-2.5">
              <LegendRow color="bg-emerald-500" label={t('learner.cards.atAGlance.passed')} value={reported} />
              <LegendRow color="bg-amber-500" label={t('learner.cards.atAGlance.partial')} value={clicked} />
              <LegendRow color="bg-red-500" label={t('learner.cards.atAGlance.failed')} value={missed} />
            </div>
          </div>

          {/* Streak current */}
          <div className="rounded-xl border border-border bg-gradient-to-br from-orange-500/15 via-red-500/5 to-transparent p-3">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>{t('learner.cards.atAGlance.streak')}</span>
              <Flame className="h-3.5 w-3.5 text-orange-500" />
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-2xl font-bold tabular-nums text-foreground">{streak.current}</span>
              <span className="text-[11px] text-muted-foreground">{t('learner.cards.atAGlance.current')}</span>
            </div>
            <div className="text-[10px] text-muted-foreground">{t('learner.cards.atAGlance.best', { count: streak.best })}</div>
          </div>

          {/* Pillar Slam */}
          <div className="rounded-xl border border-border bg-gradient-to-br from-amber-500/15 via-yellow-500/5 to-transparent p-3">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>{t('learner.cards.common.pillarSlam')}</span>
              <StarIcon size={14} />
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-2xl font-bold tabular-nums text-foreground">{pillarSlam.done}</span>
              <span className="text-[11px] text-muted-foreground">{t('learner.cards.common.ofTotal', { total: pillarSlam.total })}</span>
            </div>
            <Progress value={(pillarSlam.done / pillarSlam.total) * 100} className="mt-1.5 h-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function LegendRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
      <span className="text-sm font-medium tabular-nums w-8 text-end text-foreground">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}
